// src/pages/Home.jsx
import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import SEO from "../components/SEO";
import { motion } from "framer-motion";

import Hero from "../components/HeroCSS";
import About from "../components/Sections/AboutPreview";
import ServicesPreview from "../components/Sections/ServicesPreview";
import ProjectsPreview from "../components/Sections/ProjectsPreview";
import CoursesPreview from "../components/Sections/CoursesPreview";
import ContactPreview from "../components/Sections/ContactPreview";
import ServicesSection from "../components/Sections/ServicesSection";

export default function Home() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const prefill = {
    type: params.get("type") || "",
    serviceOrCourse: params.get("title") || "",
  };

  const currentUrl =
    typeof window !== "undefined" ? window.location.href : "https://ankielabs.com";

  // Scroll to top on page load and dynamic scroll-based titles for home sections
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    const handleScroll = () => {
      // If the user is at the very top of the page, keep the main home title
      if (window.scrollY < 100) {
        document.title = "Ankielabs — Creative 3D Production | VFX | Branding | Skill Labs";
        return;
      }

      const scrollPos = window.scrollY + window.innerHeight / 3;

      const servicesEl = document.getElementById("services") || document.getElementById("service");
      const coursesEl = document.getElementById("skillLabs");
      const contactEl = document.getElementById("contact");

      if (contactEl && scrollPos >= contactEl.offsetTop) {
        document.title = "Contact Us — Ankielabs";
      } else if (coursesEl && scrollPos >= coursesEl.offsetTop) {
        document.title = "Skill Labs Courses — Ankielabs Training";
      } else if (servicesEl && scrollPos >= servicesEl.offsetTop) {
        document.title = "3D Services & Creative Production — Ankielabs";
      } else {
        document.title = "Ankielabs — Creative 3D Production | VFX | Branding | Skill Labs";
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return (
    <main className="overflow-hidden">

      {/* 🔥 SEO for Home Page */}
      <SEO
        title="Ankielabs — Creative 3D Production | VFX | Branding | Skill Labs"
        description="Ankielabs provides industry-leading 3D production, modeling, texturing, lighting, VFX, branding, website development, and professional Skill Labs courses."
        image="https://ankielabs.com/images/seo-home.webp"
        url={currentUrl}
      />

      {/* Hero Section */}
      <motion.section
        id="hero"
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        <Hero />
      </motion.section>

      {/* Services Main Section */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <ServicesSection />
      </motion.div>

      {/* About */}
      <SectionWrapper id="about">
        <About />
      </SectionWrapper>

      {/* Services Preview */}
      <SectionWrapper id="service">
        <ServicesPreview />
      </SectionWrapper>

      {/* Projects Preview */}
      <SectionWrapper id="projects">
        <ProjectsPreview />
      </SectionWrapper>

      {/* Courses Preview */}
      <SectionWrapper id="skillLabs">
        <CoursesPreview />
      </SectionWrapper>

      {/* Contact */}
      <SectionWrapper id="contact">
        <ContactPreview prefill={prefill} />
      </SectionWrapper>
    </main>
  );
}

/* ---------------------------
   Reusable Wrapper Component
--------------------------- */
function SectionWrapper({ id, children }) {
  return (
    <motion.section
      id={id}
      className="py-20 md:py-28 px-4 md:px-8"
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      viewport={{ once: true }}
    >
      {children}
    </motion.section>
  );
}
